import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Download,
  Smartphone,
  ShieldCheck,
  Users,
  Activity,
  Star,
  Calendar,
  CloudSun,
  Bell,
  X,
  Clock,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const PWADesktop = () => {
  const navigate = useNavigate();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setBannerVisible(true);
    };

    const handleInstalled = () => {
      setBannerVisible(false);
      setInstallPrompt(null);
      toast.success('MyStoryMe is now installed!');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('Installing MyStoryMe…');
      }
      setInstallPrompt(null);
      setBannerVisible(false);
    } else {
      toast('Open your browser menu and choose “Install app” or “Add to Home Screen”.', {
        icon: '📲',
      });
    }
  };

  const handleCreateProfile = () => navigate('/profiles/create');
  const handleViewStories = () => navigate('/stories');

  const quickStats = [
    { label: 'Daily Readers', value: '2,431', accent: 'text-blue-500' },
    { label: 'Stories Created', value: '38', accent: 'text-purple-500' },
    { label: 'Avg. Session', value: '12m', accent: 'text-pink-500' },
  ];

  const highlightBadges = [
    { label: 'Offline Ready', icon: <CloudSun className="h-4 w-4" /> },
    { label: 'Secure Sync', icon: <ShieldCheck className="h-4 w-4" /> },
    { label: 'Real-time Alerts', icon: <Bell className="h-4 w-4" /> },
  ];

  const featureCards = [
    {
      title: 'Install the App',
      detail: 'Get instant access with offline support and push alerts.',
      icon: <Download className="h-6 w-6 text-purple-500" />,
      action: handleInstallClick,
    },
    {
      title: 'Create Profiles',
      detail: 'Craft a magical identity for every child in minutes.',
      icon: <Users className="h-6 w-6 text-pink-500" />,
      action: handleCreateProfile,
    },
    {
      title: 'Review Insights',
      detail: 'Track engagement, favorites, and reading streaks.',
      icon: <Activity className="h-6 w-6 text-blue-500" />,
      action: handleViewStories,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />

      {bannerVisible && (
        <div className="bg-slate-900 text-white py-4 px-4">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Smartphone className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-lg">Install MyStoryMe</p>
                <p className="text-sm text-slate-300">
                  Enjoy offline access, instant alerts, and a desktop-grade experience.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleInstallClick}
                className="px-5 py-2 rounded-lg bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors"
              >
                Install
              </button>
              <button
                type="button"
                onClick={() => setBannerVisible(false)}
                className="p-2 text-slate-300 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 container mx-auto px-4 py-10 relative">
        <div className="hidden md:block absolute inset-0 pointer-events-none">
          <div className="w-40 h-40 bg-purple-200 rounded-full opacity-30 blur-3xl absolute -top-10 -left-10" />
          <div className="w-32 h-32 bg-pink-200 rounded-full opacity-30 blur-3xl absolute top-10 right-10" />
          <div className="w-28 h-28 bg-yellow-200 rounded-full opacity-30 blur-3xl absolute bottom-10 left-1/3" />
        </div>

        <section className="grid lg:grid-cols-2 gap-10 items-center relative z-10">
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-white/70 rounded-full border border-purple-100 text-purple-700 font-semibold text-sm mb-6 shadow-sm">
              <Sparkles className="h-4 w-4 mr-2" />
              Progressive Web App
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              The full MyStoryMe experience on your desktop, tablet, or TV.
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Install the PWA to unlock offline reading, immersive presentations, and instant notifications
              every time a story is ready.
            </p>

            <div className="flex flex-wrap gap-4 mb-6">
              <button
                type="button"
                onClick={handleCreateProfile}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
              >
                Create Profile
              </button>
              <button
                type="button"
                onClick={handleInstallClick}
                className="px-6 py-3 border border-purple-200 text-purple-700 bg-white rounded-xl font-semibold hover:border-purple-400 transition-colors"
              >
                Install App
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {quickStats.map((item) => (
                <div key={item.label} className="bg-white rounded-2xl shadow-lg border border-purple-50 p-4 text-center">
                  <div className={`text-2xl font-bold ${item.accent}`}>{item.value}</div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-pink-200 rounded-full opacity-50 blur-3xl" />
            <div className="relative bg-white rounded-[2rem] shadow-2xl border border-purple-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-500">Today</p>
                  <p className="text-2xl font-bold text-gray-900">Story Studio</p>
                </div>
                <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full">
                  Live
                </span>
              </div>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-4 text-white shadow-lg">
                  <p className="text-sm opacity-80">Current adventure</p>
                  <p className="text-xl font-semibold">Avery & The Moonlit Library</p>
                  <p className="text-sm mt-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Reading time: 8 min
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 rounded-xl p-4">
                    <p className="text-sm text-purple-600 font-semibold mb-2 flex items-center gap-2">
                      <Star className="h-4 w-4" />
                      Favorites
                    </p>
                    <p className="text-3xl font-bold text-purple-900">18</p>
                    <p className="text-xs text-purple-500">+4 this week</p>
                  </div>
                  <div className="bg-pink-50 rounded-xl p-4">
                    <p className="text-sm text-pink-600 font-semibold mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      This Month
                    </p>
                    <p className="text-3xl font-bold text-pink-900">7</p>
                    <p className="text-xs text-pink-500">Stories shared</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-10 left-8 bg-white rounded-2xl shadow-xl border border-purple-100 p-4 w-64">
              <p className="text-sm text-gray-500 mb-2">Quick Actions</p>
              <div className="flex flex-col gap-3">
                {featureCards.map((card) => (
                  <button
                    key={card.title}
                    type="button"
                    onClick={card.action}
                    className="flex items-center gap-3 text-left px-3 py-2 rounded-xl border border-gray-100 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                  >
                    {card.icon}
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{card.title}</p>
                      <p className="text-xs text-gray-500">{card.detail}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-16 relative z-10">
          <div className="bg-white rounded-3xl shadow-2xl border border-purple-100 p-8">
            <div className="flex flex-wrap gap-4 mb-8">
              {highlightBadges.map((badge) => (
                <span
                  key={badge.label}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium"
                >
                  {badge.icon}
                  {badge.label}
                </span>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="rounded-2xl border border-purple-100 p-6 bg-gradient-to-br from-purple-50 to-white">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Desktop-First Design</h3>
                <p className="text-gray-600 mb-5">
                  Enjoy a widescreen canvas for story editing, live previews, and parental controls.
                </p>
                <button
                  type="button"
                  onClick={handleViewStories}
                  className="text-purple-600 font-semibold hover:text-purple-800"
                >
                  Explore Stories →
                </button>
              </div>
              <div className="rounded-2xl border border-pink-100 p-6 bg-gradient-to-br from-pink-50 to-white">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Device Sync</h3>
                <p className="text-gray-600 mb-5">
                  Start on desktop, continue on tablet, and finish on TV night with instant syncing.
                </p>
                <button
                  type="button"
                  onClick={handleCreateProfile}
                  className="text-pink-600 font-semibold hover:text-pink-800"
                >
                  Create Profile →
                </button>
              </div>
              <div className="rounded-2xl border border-yellow-100 p-6 bg-gradient-to-br from-yellow-50 to-white">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Parental Peace of Mind</h3>
                <p className="text-gray-600 mb-5">
                  Manage privacy, downloads, and notifications with enterprise-grade security.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/profiles')}
                  className="text-yellow-600 font-semibold hover:text-yellow-700"
                >
                  Manage Profiles →
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PWADesktop;

